import React, { useState } from 'react';
import '../components/newevent.css';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../config/supabaseClient';
import { usePassport } from '../passport';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '../icons/closeicon';
import ForwardsAndBackwardsIcon from '../icons/forwardsandbackwardsicon';
import RocketIcon from '../icons/rocketicon';


export default function NewEvent() {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [imageURL, setImageURL] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const navigate = useNavigate();
  const locationHook = useLocation();
  const { user: passportUser, profile } = usePassport();

  if (locationHook.pathname !== '/newevent') return null;

  const generateRandomVRC = () =>
    Math.random().toString(36).substring(2, 8).toUpperCase();

  const generateUniqueVRC = async () => {
    let newCode = '';
    let isUnique = false;

    while (!isUnique) {
      newCode = generateRandomVRC();
      const { data, error } = await supabase
        .from('hosting')
        .select('vrc')
        .eq('vrc', newCode);

      if (!error && (!data || data.length === 0)) {
        isUnique = true;
      }
    }

    return newCode;
  };

  const geocodeLocation = async (locationString) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationString)}&format=json&limit=1`
    );
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon),
      };
    } else {
      return null;
    }
  };

  const handleCreateEvent = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!title || !imageURL) {
      alert('Please fill in both title and image URL.');
      setIsSaving(false);
      return;
    }

    if (!passportUser) {
      alert('You must be logged in to create an event.');
      setIsSaving(false);
      return;
    }

    try {
      const vrc = await generateUniqueVRC();
      const coords = await geocodeLocation(location);

      const payload = {
        event_name: title,
        event_date: date,
        event_location: location,
        event_image: imageURL,
        user_id: passportUser.id,
        host_id: passportUser.id,
        vrc,
        host_qr_token: uuidv4(),
        event_lat: coords?.lat || null,
        event_lon: coords?.lon || null,
        moderator_contact:
          `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim() || passportUser.email,
      };

      const { data, error } = await supabase
        .from('hosting')
        .insert([payload])
        .select('id')
        .single();

      if (error || !data) {
        alert('❌ Failed to create event.');
        console.error(error);
      } else {
        console.log("🌐 Navigating to event:", data.id);
        navigate(`/event/${data.id}`);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('Something went wrong.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      {step === 1 && (
        <div className="modal">
              <div className="modal-close-wrapper" onClick={() => navigate('/dashboard')}>
                <CloseIcon className="modal-close-icon" size={24} />
              </div>
          <h2 className="modal-title">What's your event called?</h2>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter event title"
            className="modal-input"
          />
        <div className="modal-nav-single">
          <ForwardsAndBackwardsIcon
            direction="right"
            onClick={() => title.trim() && setStep(2)}
            disabled={!title.trim()}
          />
        </div>
      </div>
      )}


      {step === 2 && (
        <div className="modal">
          <div className="modal-close-wrapper" onClick={() => navigate('/dashboard')}>
            <CloseIcon className="modal-close-icon" size={24} />
          </div>

          <h2 className="modal-title">Event Date?</h2>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="modal-input"
          />

          <div className="modal-nav-dual">
            <ForwardsAndBackwardsIcon
              direction="left"
              onClick={() => setStep(1)}
            />
            <ForwardsAndBackwardsIcon
              direction="right"
              onClick={() => date && setStep(3)}
              disabled={!date}
            />
          </div>
        </div>
      )}


     {step === 3 && (
      <div className="modal">
        <div className="modal-close-wrapper" onClick={() => navigate('/dashboard')}>
          <CloseIcon className="modal-close-icon" size={24} />
        </div>

        <h2 className="modal-title">Where is the event?</h2>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Enter event location"
          className="modal-input"
        />

        <div className="modal-nav-dual">
          <ForwardsAndBackwardsIcon
            direction="left"
            onClick={() => setStep(2)}
          />
          <ForwardsAndBackwardsIcon
            direction="right"
            onClick={() => location.trim() && setStep(4)}
            disabled={!location.trim()}
          />
        </div>
      </div>
    )}


      {step === 4 && (
        <div className="modal">
          <div className="modal-close-wrapper" onClick={() => navigate('/dashboard')}>
            <CloseIcon className="modal-close-icon" size={24} />
          </div>

          <h2 className="modal-title">Want to add an image for your event?</h2>
          <input
            type="text"
            value={imageURL}
            onChange={(e) => setImageURL(e.target.value)}
            placeholder="Paste image URL here"
            className="modal-input"
          />

          {imageURL && (
            <img
              src={imageURL}
              alt="Event Preview"
              className="event-preview-image"
              style={{ marginTop: '1rem', maxWidth: '100%', borderRadius: '0.5rem' }}
            />
          )}

          <div className="modal-nav-dual">
            <ForwardsAndBackwardsIcon
              direction="left"
              onClick={() => setStep(3)}
            />
            <ForwardsAndBackwardsIcon
              direction="right"
              onClick={() => imageURL.trim() && setStep(5)}
              disabled={!imageURL.trim()}
            />
          </div>
        </div>
      )}


      {step === 5 && (
        <div className="modal">
          <div className="modal-close-wrapper" onClick={() => navigate('/dashboard')}>
            <CloseIcon className="modal-close-icon" size={24} />
          </div>

          <h2 className="modal-title">Review and Create Event</h2>

          <p><strong>Title:</strong> {title}</p>
          <p><strong>Date:</strong> {new Date(date).toLocaleDateString()}</p>
          <p><strong>Location:</strong> {location}</p>

          {/\.(jpeg|jpg|gif|png|webp)$/i.test(imageURL.trim()) ? (
            <img
              src={imageURL.trim()}
              alt="Event preview"
              className="event-preview-image"
            />
          ) : (
            <div className="event-image-placeholder">
              Sure you don’t want an image for your event?<br />
              You can always add one later.
            </div>
          )}


          <div className="modal-nav-dual">
            <ForwardsAndBackwardsIcon direction="left" onClick={() => setStep(4)} />
            <RocketIcon onClick={handleCreateEvent} disabled={isSaving} />
          </div>
        </div>
      )}

    </div>
  );
}
